import { useEffect, useRef, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { AlertTriangle, FlaskConical } from "lucide-react";
import { cn } from "@/lib/cn";
import { ROTULO_DO_ALERTA, type AlertaDaAnamnese, type AppointmentStatus } from "@bookdental/schemas";
import { Popover, PopoverArrow, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import type { OpenProsthesisOrderSummary } from "./prosthesis-order-summary";
import {
  heightForDuration,
  minutesToTime,
  positionYFor,
  PIXELS_PER_MINUTE,
  timeToMinutes,
} from "@/lib/agenda-time";
import { getCardStyleSpec } from "@/lib/agenda-card-styles";
import { AppointmentPopover } from "./AppointmentPopover";

type Appointment = {
  id: string;
  patientId?: string | null;
  patientName: string;
  patientPhone?: string | null;
  procedure: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  color?: string | null;
  dentistName?: string | null;
  sala?: { nome: string; cor: string | null } | null;
  notes?: string | null;
  date: Date | string;
  createdAt?: Date | string;
  openProsthesisOrders?: OpenProsthesisOrderSummary[];
  /** Códigos de alerta da última anamnese respondida (só chega pra quem pode ver). */
  alertasDaAnamnese?: string[];
  /** Presente = compromisso de OUTRA organização (Agenda Pessoal, visão "Todas"/"Organizações") —
   *  card fica só-leitura: sem drag, sem resize, popover mostra "Ver ficha" em vez de editar. */
  organization?: { id: string; name: string } | null;
};

type Props = {
  appointment: Appointment;
  /** Detalhe da consulta abre embaixo do card (vista de dia, onde do lado não cabe). */
  popoverEmbaixo?: boolean;
  startHour: string;
  endHour: string;
  slotDurationMinutes: number;
  resolvedColor: string;
  column: number;
  totalColumns: number;
  /** Quantas colunas o card ocupa a partir de `column`. Default 1. */
  colSpan?: number;
  /** True quando o dia do card é o dia de hoje — vindo do pai que já sabe
   *  disso via isSameDay. Evita bugs de timezone ao comparar no próprio card. */
  dayIsToday?: boolean;
  /** Minuto atual (HH*60 + MM) se for hoje; null caso contrário. Vem do pai
   *  atualizado a cada minuto — garante que data-crosses-now fica sempre
   *  sincronizado mesmo com a aba aberta por horas. */
  nowMinutesIfToday?: number | null;
  cardStyle:
    | "colorful"
    | "light"
    | "assorted"
    | "bordered"
    | "solid"
    | "minimal"
    | "outlined"
    | "sticker"
    | "bordered-thin"
    | "bordered-strong"
    | "glass"
    | "ribbon";
  /** Se true, mostra "Dr. / Dra. + primeiro nome" do profissional. */
  showDentist?: boolean;
  /** Prefixo resolvido pro dentista deste card ("Dr." | "Dra." | ...). */
  dentistTitle?: string;
  /** Se true, mostra "08:00 → 08:30" antes do nome do paciente. */
  showTime?: boolean;
  /** Se true, card esta sendo arrastado — vira silhueta pontilhada (origem). */
  isBeingDragged?: boolean;
  onEdit?: (id: string) => void;
  onStatusChange?: (id: string, newStatus: AppointmentStatus) => void;
  onDelete?: (id: string) => void;
  onIniciarAtendimento?: (id: string) => void;
  onEncerrarAtendimento?: (id: string) => void;
  atendimentoPendente?: boolean;
  onResize?: (appointmentId: string, newStart: string, newEnd: string) => void;
  onHover?: (
    range:
      | { date: Date; start: string; end: string; skipSlotHighlight?: boolean }
      | null,
  ) => void;
};

type ResizeState = {
  edge: "top" | "bottom";
  startY: number;
  origStartMin: number;
  origEndMin: number;
  currentStart: string;
  currentEnd: string;
};

export function AppointmentCard({
  appointment,
  popoverEmbaixo = false,
  startHour,
  endHour,
  slotDurationMinutes,
  resolvedColor,
  column,
  totalColumns,
  colSpan = 1,
  dayIsToday = false,
  nowMinutesIfToday = null,
  cardStyle,
  showDentist = false,
  dentistTitle,
  showTime = false,
  isBeingDragged,
  onEdit,
  onStatusChange,
  onDelete,
  onIniciarAtendimento,
  onEncerrarAtendimento,
  atendimentoPendente,
  onResize,
  onHover,
}: Props) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);
  resizeRef.current = resize;
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  /** Trava clicks/popover por um curto período após terminar um resize —
   *  evita abrir o popover como efeito colateral do pointerup. */
  const suppressClickRef = useRef(false);

  const readOnly = !!appointment.organization;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: appointment.id,
    data: { appointment },
    disabled: resize !== null || readOnly,
  });

  useEffect(() => {
    if (!resize) return;

    const onMove = (ev: PointerEvent) => {
      const latest = resizeRef.current;
      if (!latest) return;
      const deltaY = ev.clientY - latest.startY;
      const deltaMin =
        Math.round(deltaY / PIXELS_PER_MINUTE / slotDurationMinutes) *
        slotDurationMinutes;

      const startHourMin = timeToMinutes(startHour);
      const endHourMin = timeToMinutes(endHour);

      if (latest.edge === "top") {
        const newStartMin = Math.max(
          startHourMin,
          Math.min(
            latest.origStartMin + deltaMin,
            latest.origEndMin - slotDurationMinutes,
          ),
        );
        const next: ResizeState = {
          ...latest,
          currentStart: minutesToTime(newStartMin),
          currentEnd: minutesToTime(latest.origEndMin),
        };
        resizeRef.current = next;
        setResize(next);
      } else {
        const newEndMin = Math.min(
          endHourMin,
          Math.max(
            latest.origEndMin + deltaMin,
            latest.origStartMin + slotDurationMinutes,
          ),
        );
        const next: ResizeState = {
          ...latest,
          currentStart: minutesToTime(latest.origStartMin),
          currentEnd: minutesToTime(newEndMin),
        };
        resizeRef.current = next;
        setResize(next);
      }
    };

    const onUp = () => {
      const latest = resizeRef.current;
      if (latest) {
        const changed =
          latest.currentStart !== minutesToTime(latest.origStartMin) ||
          latest.currentEnd !== minutesToTime(latest.origEndMin);
        if (changed && onResizeRef.current) {
          onResizeRef.current(
            appointment.id,
            latest.currentStart,
            latest.currentEnd,
          );
        }
      }
      setResize(null);
      // Suprime o click sintético que o browser dispara após pointerup —
      // evita abrir o popover acidentalmente ao soltar o resize.
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 350);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [resize, startHour, endHour, slotDurationMinutes, appointment.id]);

  const handleResizeStart = (edge: "top" | "bottom") => (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setResize({
      edge,
      startY: e.clientY,
      origStartMin: timeToMinutes(appointment.startTime),
      origEndMin: timeToMinutes(appointment.endTime),
      currentStart: appointment.startTime,
      currentEnd: appointment.endTime,
    });
  };

  // Durante resize, o visual tem que acompanhar o mousemove em tempo real —
  // senão o card fica parado até o mouseup comitar (sensação de delay).
  // `resize.currentStart/End` atualizam a cada pointermove já snapped no
  // slot; ler daqui dá feedback instantâneo sem esperar o round-trip de
  // re-render via parent + react-query.
  const effectiveStart = resize ? resize.currentStart : appointment.startTime;
  const effectiveEnd = resize ? resize.currentEnd : appointment.endTime;
  const cardTop = positionYFor(effectiveStart, startHour);
  const cardDuration =
    timeToMinutes(effectiveEnd) - timeToMinutes(effectiveStart);
  const cardHeight = Math.max(22, heightForDuration(cardDuration));
  // O procedimento aparece no card: linha própria quando cabe, senão depois do nome.
  const procedimento = appointment.procedure?.trim() || null;
  const procedimentoEmLinha = !!procedimento && cardHeight >= 44;

  // Só o card que CRUZA a linha do horário atual marca data-crosses-now.
  // nowMinutesIfToday vem do DayColumn (atualiza a cada minuto) — garante
  // que o marcador fica sempre correto mesmo com aba aberta por muito tempo.
  const crossesNowLine = (() => {
    if (!dayIsToday || nowMinutesIfToday == null) return false;
    const startMin = timeToMinutes(appointment.startTime);
    const endMin = timeToMinutes(appointment.endTime);
    return startMin <= nowMinutesIfToday && nowMinutesIfToday < endMin;
  })();

  const colWidthPct = 100 / totalColumns;
  const widthPct = colWidthPct * colSpan;
  const leftPct = column * colWidthPct;
  // Gap fica menor conforme mais colunas visíveis no slot pra priorizar espaço pro nome.
  // Usa totalColumns - colSpan + 1 = "quantas fatias visualmente concorrem com essa"
  const visualSlots = Math.max(1, totalColumns - (colSpan - 1));
  const gapPx = visualSlots >= 4 ? 1 : visualSlots >= 3 ? 2 : 4;
  const offsetPx = gapPx / 2;
  const horizontalPadding = visualSlots >= 3 ? "px-1" : "px-1.5";
  // Espaço reservado à DIREITA da coluna pra clique livre — permite criar
  // nova consulta no mesmo horário sem abrir card existente.
  const clickZoneRightPx = 12;

  const isCancelled = appointment.status === "CANCELLED";

  const cardVisual = getCardStyleSpec(resolvedColor, cardStyle);

  return (
    <>
      <Popover
        open={popoverOpen}
        onOpenChange={(open) => {
          // Bloqueia o popover se acabou de terminar um resize
          if (open && suppressClickRef.current) return;
          setPopoverOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <button
            ref={setNodeRef}
            type="button"
            data-appointment-card="1"
            data-crosses-now={crossesNowLine ? "true" : undefined}
            onDoubleClick={(e) => e.stopPropagation()}
            onMouseEnter={() =>
              onHover?.({
                date: new Date(appointment.date),
                start: appointment.startTime,
                end: appointment.endTime,
                skipSlotHighlight: true,
              })
            }
            onMouseLeave={() => onHover?.(null)}
            {...(readOnly ? {} : listeners)}
            {...(readOnly ? {} : attributes)}
            className={`group absolute flex flex-col items-stretch justify-start rounded-[3px] ${horizontalPadding} py-1 text-left text-[11px] font-semibold leading-tight shadow-sm transition-[box-shadow,transform] duration-75 ease-out will-change-transform hover:z-30 hover:-translate-y-px hover:shadow-lg ${
              cardVisual.textClassName ?? ""
            } ${isCancelled ? "opacity-60" : ""} ${
              readOnly
                ? "cursor-pointer opacity-80 ring-1 ring-dashed ring-zinc-400/50"
                : isBeingDragged || isDragging
                  ? "cursor-grabbing opacity-40 ring-2 ring-dashed ring-zinc-400/60 shadow-none"
                  : "cursor-grab"
            } ${resize ? "ring-2 ring-brand-400" : ""}`}
            style={{
              top: cardTop,
              height: cardHeight,
              // Cards ocupam (100% - clickZoneRightPx) da coluna; o espaço
              // que sobra à direita aceita clique pra criar nova consulta.
              left: `calc((100% - ${clickZoneRightPx}px) * ${leftPct / 100} + ${offsetPx}px)`,
              width: `calc((100% - ${clickZoneRightPx}px) * ${widthPct / 100} - ${gapPx}px)`,
              touchAction: "none",
              // Durante o resize, sinaliza ao compositor que top/height vão
              // mudar a cada frame — move pra GPU layer e evita reflow
              // cascade em outros elementos da agenda.
              willChange: resize ? "top, height" : undefined,
              ...cardVisual.style,
            }}
          >
            {/* Tooltip com intervalo de horário — aparece no hover, CSS puro, sem delay */}
            <span
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-0 z-50 -translate-x-1/2 -translate-y-[calc(100%+4px)] whitespace-nowrap rounded-md bg-zinc-700/95 px-2 py-0.5 text-[10px] font-bold tabular-nums text-zinc-50 opacity-0 shadow-lg ring-1 ring-white/10 backdrop-blur-sm transition-opacity duration-75 group-hover:opacity-100 dark:bg-zinc-200/95 dark:text-zinc-800 dark:ring-black/10"
            >
              {appointment.startTime} – {appointment.endTime}
            </span>

            {/* Badge de pedido de prótese em aberto pro paciente deste compromisso */}
            {appointment.openProsthesisOrders && appointment.openProsthesisOrders.length > 0 && (
              <span
                className="absolute right-0.5 top-0.5 z-20 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white/90 text-brand-600 shadow-sm dark:bg-black/40 dark:text-primary"
                title="Pedido de prótese em aberto"
              >
                <FlaskConical className="h-2.5 w-2.5" />
              </span>
            )}

            {/* Alerta da anamnese (alergia, anticoagulante...) — o detalhe fica na ficha */}
            {appointment.alertasDaAnamnese && appointment.alertasDaAnamnese.length > 0 && (
              <span
                className={cn(
                  "absolute top-0.5 z-20 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-100 text-amber-700 shadow-sm",
                  appointment.openProsthesisOrders?.length ? "right-[18px]" : "right-0.5",
                )}
                title={`Atenção: ${appointment.alertasDaAnamnese.map((x) => ROTULO_DO_ALERTA[x as AlertaDaAnamnese] ?? x).join(", ")}`}
              >
                <AlertTriangle className="h-2.5 w-2.5" />
              </span>
            )}

            {/* Conteúdo do card com overflow próprio pra não vazar pelo tooltip */}
            {!readOnly && (
              <span
                onPointerDown={handleResizeStart("top")}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                style={{ touchAction: "none" }}
                className="pointer-events-none absolute inset-x-0 top-0 z-10 h-2 cursor-row-resize opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 hover:bg-zinc-900/15"
                aria-label="Ajustar início"
              />
            )}

            {/* Em cards ALTOS (>= 36px) as linhas extras aparecem completas
                acima do nome. Em cards curtos o prefixo vira inline pra
                caber tudo em uma linha só com o máximo do nome. */}
            {showTime && cardHeight >= 36 && (
              <div className="truncate text-[10px] font-medium tabular-nums tracking-tight opacity-65">
                {appointment.startTime}
                <span className="mx-0.5 opacity-70">→</span>
                {appointment.endTime}
              </div>
            )}
            {((showDentist && appointment.dentistName) || appointment.sala) && !readOnly && cardHeight >= 36 && (
              <div
                className="truncate text-[10px] font-semibold uppercase tracking-wide opacity-70"
                title={[appointment.dentistName && `${dentistTitle ?? "Dr."} ${stripTitlePrefix(appointment.dentistName)}`, appointment.sala?.nome].filter(Boolean).join(" · ")}
              >
                {showDentist && appointment.dentistName && (
                  <>
                    {dentistTitle ?? "Dr."} {stripTitlePrefix(appointment.dentistName).split(/\s+/)[0]}
                  </>
                )}
                {appointment.sala && (
                  <>
                    {showDentist && appointment.dentistName && <span className="mx-1 opacity-60">·</span>}
                    <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ backgroundColor: appointment.sala.cor ?? "currentColor" }} />
                    {appointment.sala.nome}
                  </>
                )}
              </div>
            )}
            {readOnly && appointment.organization && cardHeight >= 36 && (
              <div
                className="truncate text-[10px] font-semibold uppercase tracking-wide opacity-70"
                title={appointment.organization.name}
              >
                {appointment.organization.name}
              </div>
            )}
            <div
              lang="pt-BR"
              className={`break-words ${isCancelled ? "line-through" : ""}`}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: Math.max(1, Math.floor((cardHeight - (procedimentoEmLinha ? 14 : 0)) / 14)),
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                hyphens: "auto",
              }}
            >
              {showDentist &&
                appointment.dentistName &&
                cardHeight < 36 && (
                  <span className="font-semibold opacity-70">
                    {dentistTitle ?? "Dr."}{" "}
                    {stripTitlePrefix(appointment.dentistName).split(/\s+/)[0]}
                    <span className="mx-1 opacity-60">·</span>
                  </span>
                )}
              {appointment.patientName}
              {procedimento && !procedimentoEmLinha && (
                <span className="opacity-65">
                  <span className="mx-1 opacity-60">·</span>
                  {procedimento}
                </span>
              )}
            </div>
            {procedimentoEmLinha && (
              <div className="truncate text-[10.5px] opacity-70" title={procedimento!}>
                {procedimento}
              </div>
            )}

            {!readOnly && (
              <span
                onPointerDown={handleResizeStart("bottom")}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                style={{ touchAction: "none" }}
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-2 cursor-row-resize opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 hover:bg-zinc-900/15"
                aria-label="Ajustar fim"
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={popoverEmbaixo ? "bottom" : "right"}
          align="start"
          sideOffset={popoverEmbaixo ? 6 : 10}
          collisionPadding={12}
          className="w-auto border-zinc-200 bg-white p-4 shadow-2xl"
        >
          <PopoverArrow
            className="fill-white"
            width={14}
            height={8}
            style={{ filter: "drop-shadow(-1px 0 0 rgb(228 228 231))" }}
          />
          <AppointmentPopover
            appointment={appointment}
            readOnly={readOnly}
            organizationName={appointment.organization?.name}
            onEdit={() => {
              setPopoverOpen(false);
              onEdit?.(appointment.id);
            }}
            onStatusChange={(s) => {
              onStatusChange?.(appointment.id, s);
            }}
            onDelete={() => {
              setPopoverOpen(false);
              onDelete?.(appointment.id);
            }}
            onIniciarAtendimento={
              onIniciarAtendimento
                ? () => {
                    setPopoverOpen(false);
                    onIniciarAtendimento(appointment.id);
                  }
                : undefined
            }
            onEncerrarAtendimento={
              onEncerrarAtendimento
                ? () => {
                    setPopoverOpen(false);
                    onEncerrarAtendimento(appointment.id);
                  }
                : undefined
            }
            atendimentoPendente={atendimentoPendente}
            onClose={() => setPopoverOpen(false)}
          />
        </PopoverContent>
      </Popover>

      {/* Linha de resize em movimento */}
      {resize && (
        <>
          <div
            className="pointer-events-none absolute left-0 right-0 z-20 h-[2px]"
            style={{
              top:
                resize.edge === "top"
                  ? positionYFor(resize.currentStart, startHour)
                  : positionYFor(resize.currentEnd, startHour),
              background: resolvedColor,
              boxShadow: `0 0 0 1px #fff`,
            }}
          />
          <div
            className="pointer-events-none absolute z-20 rounded px-1.5 py-0.5 text-[9px] font-semibold text-white shadow-md"
            style={{
              top:
                (resize.edge === "top"
                  ? positionYFor(resize.currentStart, startHour)
                  : positionYFor(resize.currentEnd, startHour)) - 10,
              left: `calc(${leftPct}% + 4px)`,
              background: resolvedColor,
            }}
          >
            {resize.edge === "top" ? resize.currentStart : resize.currentEnd}
          </div>
        </>
      )}
    </>
  );
}

/** Remove prefixos de título já embutidos no nome (Dr., Dra., Prof., Enf.)
 *  antes de aplicar o prefixo configurado. Evita "Dr. Dr. Luiz". */
function stripTitlePrefix(name: string): string {
  const TITLE_RE = /^(dr\.?|dra\.?|prof\.?|profa\.?|enf\.?|enfa\.?)\s+/i;
  let cleaned = name.trim();
  while (TITLE_RE.test(cleaned)) {
    cleaned = cleaned.replace(TITLE_RE, "").trim();
  }
  return cleaned;
}
